import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import TopBar from './TopBar'
import PaginaNotifiche from './PaginaNotifiche'

export default function PaginaAdesioni({ onCambiaTab, coachPremium, onRichiediUpgrade }) {
  const { profilo } = useAuth()
  const isCoach = profilo?.piano === 'coach' || profilo?.piano === 'admin'
  const isAdmin = profilo?.piano === 'admin'

  return isCoach
    ? <AadesionisCoach profilo={profilo} onCambiaTab={onCambiaTab} coachPremium={isAdmin || coachPremium} onRichiediUpgrade={onRichiediUpgrade} />
    : <AdesioniAtleta profilo={profilo} onCambiaTab={onCambiaTab} />
}

/**
 * Avvisi mandati agli atleti quando il coach cambia lo stato di un appuntamento.
 *
 * Prima il testo era un ternario su 'confermato': qualunque stato diverso —
 * quindi anche la RIAPERTURA — prendeva il testo dell'annullamento. Riaprire un
 * appuntamento mandava a tutto il gruppo "L'allenamento è stato annullato",
 * cioè il contrario di quello che il coach aveva appena fatto.
 */
function avvisoStato(stato, titoloApp) {
  if (stato === 'confermato') return { titolo: '✅ Allenamento confermato', testo: `L'allenamento "${titoloApp}" è CONFERMATO!` }
  if (stato === 'annullato') return { titolo: '❌ Allenamento annullato', testo: `L'allenamento "${titoloApp}" è stato annullato.` }
  if (stato === 'aperto') return { titolo: '🔄 Allenamento riaperto', testo: `L'allenamento "${titoloApp}" è di nuovo aperto: dichiara se ci sei.` }
  return null
}

/* ══════════════════════════════════════════
   VISTA COACH
══════════════════════════════════════════ */
function AadesionisCoach({ profilo, onCambiaTab, coachPremium, onRichiediUpgrade }) {
  const [gruppi, setGruppi] = useState([])
  const [appuntamenti, setAppuntamenti] = useState([])
  const [appuntamentoSel, setAppuntamentoSel] = useState(null)
  const [risposte, setRisposte] = useState([])
  const [mostraForm, setMostraForm] = useState(false)
  const [gruppoForm, setGruppoForm] = useState('')
  const [titoloForm, setTitoloForm] = useState('')
  const [dataForm, setDataForm] = useState('')
  const [oraForm, setOraForm] = useState('')
  const [noteForm, setNoteForm] = useState('')
  const [minForm, setMinForm] = useState('')
  const [salvataggio, setSalvataggio] = useState(false)
  const [toast, setToast] = useState('')
  const [confermaElimina, setConfermaElimina] = useState(null)
  const [vista, setVista] = useState('prossimi') // 'prossimi' | 'passati'
  const [mostraNotifiche, setMostraNotifiche] = useState(false)
  const [contatoriRisposte, setContatoriRisposte] = useState({}) // appId -> { si, no }

  useEffect(() => { caricaGruppi(); caricaAppuntamenti() }, [])

  // Adesioni è una funzione Premium: il piano Free del coach non vi accede.
  if (!coachPremium) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <TopBar onIndietro={onCambiaTab ? () => onCambiaTab('home') : undefined} onNotifiche={undefined} />
        <div className="scroll-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ maxWidth: 340, textAlign: 'center' }}>
            <div className="card-body" style={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
              <div style={{ fontSize: 44 }}>🔒</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 17 }}>Adesioni è una funzione Premium</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
                Raccogli le adesioni dei tuoi atleti per organizzare le lezioni in palestra. Passa a Premium per sbloccarla.
              </div>
              <button className="btn btn-primary" style={{ width: '100%', marginTop: 6 }} onClick={onRichiediUpgrade}>
                ⚡ Passa a Premium — 6€/mese
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  function toast_msg(msg) { setToast(msg); setTimeout(() => setToast(''), 3000) }

  async function caricaGruppi() {
    const { data } = await supabase.from('gruppi')
      .select('id, nome')
      .eq('gestore_id', profilo.id)
      .eq('attivo', true)
      .order('creato_il', { ascending: false })
    setGruppi(data || [])
    if (data?.length && !gruppoForm) setGruppoForm(data[0].id)
  }

  async function caricaAppuntamenti() {
    const { data } = await supabase.from('adesioni_appuntamenti')
      .select('*, gruppi(nome)')
      .eq('coach_id', profilo.id)
      .order('data', { ascending: true })
      .order('ora', { ascending: true })
    setAppuntamenti(data || [])

    // Carica contatori risposte per tutti gli appuntamenti
    if (data?.length) {
      const { data: risposteAll } = await supabase.from('adesioni_risposte')
        .select('appuntamento_id, risposta')
        .in('appuntamento_id', data.map(a => a.id))
      const contatori = {}
      data.forEach(a => { contatori[a.id] = { si: 0, no: 0 } })
      risposteAll?.forEach(r => {
        if (contatori[r.appuntamento_id]) contatori[r.appuntamento_id][r.risposta]++
      })
      setContatoriRisposte(contatori)
    }
  }

  async function caricaRisposte(appId) {
    const { data } = await supabase.from('adesioni_risposte')
      .select('*, profili(nome, cognome)')
      .eq('appuntamento_id', appId)
    setRisposte(data || [])
  }

  async function apriAppuntamento(app) {
    setAppuntamentoSel(app)
    await caricaRisposte(app.id)
  }

  /** Notifica gli atleti ATTIVI del gruppo. Restituisce false se il database
   *  ha rifiutato: una notifica che non parte non deve passare inosservata,
   *  perché il coach la scoprirebbe dagli atleti che non si presentano. */
  async function notificaAtleti(gruppoId, titolo, testo, appuntamentoId) {
    const { data: membri } = await supabase.from('gruppo_membri')
      .select('utente_id').eq('gruppo_id', gruppoId).eq('stato', 'attivo')
    if (!membri?.length) return true
    const { error } = await supabase.from('notifiche').insert(
      membri.map(m => ({
        utente_id: m.utente_id,
        tipo: 'adesione',
        titolo,
        testo,
        dati: JSON.stringify({ appuntamento_id: appuntamentoId }),
      }))
    )
    if (error) { console.error('[adesioni] notifiche non inviate:', error); return false }
    return true
  }

  async function creaAppuntamento() {
    if (!gruppoForm || !titoloForm || !dataForm || !oraForm) {
      toast_msg('Compila tutti i campi obbligatori')
      return
    }
    setSalvataggio(true)
    const { data: nuovo, error } = await supabase.from('adesioni_appuntamenti').insert({
      gruppo_id: gruppoForm,
      coach_id: profilo.id,
      titolo: titoloForm,
      data: dataForm,
      ora: oraForm,
      note: noteForm || null,
      min_partecipanti: minForm ? parseInt(minForm) : 0,
      stato: 'aperto',
    }).select().single()

    if (error) { toast_msg('Errore: ' + error.message); setSalvataggio(false); return }

    const nomeGruppo = gruppi.find(g => g.id === gruppoForm)?.nome || 'gruppo'
    const dataFmt = new Date(dataForm + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })
    const inviate = await notificaAtleti(
      gruppoForm,
      '📣 Nuovo appuntamento',
      `${titoloForm} — ${dataFmt} alle ${oraForm.slice(0, 5)} (${nomeGruppo})`,
      nuovo.id,
    )

    setSalvataggio(false)
    setMostraForm(false)
    resetForm()
    toast_msg(inviate ? '✅ Appuntamento creato e atleti avvisati' : '⚠️ Appuntamento creato, ma la notifica agli atleti non è partita')
    caricaAppuntamenti()
  }

  async function cambiaStato(app, nuovoStato) {
    const { error } = await supabase.from('adesioni_appuntamenti').update({ stato: nuovoStato }).eq('id', app.id)
    if (error) { toast_msg('Errore: ' + error.message); return }

    const avviso = avvisoStato(nuovoStato, app.titolo)
    const inviate = avviso
      ? await notificaAtleti(app.gruppo_id, avviso.titolo, avviso.testo, app.id)
      : true

    toast_msg(
      !inviate ? '⚠️ Stato aggiornato, ma la notifica agli atleti non è partita'
      : nuovoStato === 'confermato' ? '✅ Confermato!'
      : nuovoStato === 'annullato' ? '❌ Annullato'
      : '🔄 Riaperto')
    setAppuntamentoSel(prev => prev ? { ...prev, stato: nuovoStato } : null)
    caricaAppuntamenti()
  }

  async function eliminaAppuntamento(id) {
    const { error } = await supabase.from('adesioni_appuntamenti').delete().eq('id', id)
    if (error) { toast_msg('Errore: ' + error.message); return }
    setConfermaElimina(null)
    setAppuntamentoSel(null)
    toast_msg('🗑️ Appuntamento eliminato')
    caricaAppuntamenti()
  }

  function resetForm() {
    setTitoloForm('')
    setDataForm('')
    setOraForm('')
    setNoteForm('')
    setMinForm('')
    if (gruppi.length) setGruppoForm(gruppi[0].id)
  }

  const ora = new Date()
  const oggi = ora.toISOString().split('T')[0]

  const appFiltrati = appuntamenti.filter(a => {
    const dataSorpAssata = a.data < oggi || (a.data === oggi && a.ora < ora.toTimeString().slice(0, 8))
    return vista === 'prossimi' ? !dataSorpAssata : dataSorpAssata
  })

  const statoColor = { aperto: 'var(--accent)', confermato: 'var(--green)', annullato: 'var(--red)' }
  const statoLabel = { aperto: '🟡 Aperto', confermato: '✅ Confermato', annullato: '❌ Annullato' }

  // Early return notifiche — sostituisce tutta la vista
  if (mostraNotifiche) return <PaginaNotifiche onChiudi={() => setMostraNotifiche(false)} onNavigate={() => setMostraNotifiche(false)} />

  // Vista dettaglio appuntamento
  if (appuntamentoSel) {
    const si = risposte.filter(r => r.risposta === 'si')
    const no = risposte.filter(r => r.risposta === 'no')
    const dataFmt = new Date(appuntamentoSel.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
        <TopBar onIndietro={() => setAppuntamentoSel(null)} onNotifiche={() => setMostraNotifiche(true)} />
        <div className="scroll-area">
          {/* Header appuntamento */}
          <div className="card">
            <div className="card-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 17 }}>{appuntamentoSel.titolo}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                    {appuntamentoSel.gruppi?.nome}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text)', marginTop: 8 }}>
                    📅 {dataFmt}<br />
                    🕐 {appuntamentoSel.ora?.slice(0, 5)}
                  </div>
                  {appuntamentoSel.note && (
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8, fontStyle: 'italic' }}>
                      {appuntamentoSel.note}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'var(--surface2)', color: statoColor[appuntamentoSel.stato] }}>
                  {statoLabel[appuntamentoSel.stato]}
                </div>
              </div>

              {appuntamentoSel.min_partecipanti > 0 && (
                <div style={{ marginTop: 12, fontSize: 12, color: si.length >= appuntamentoSel.min_partecipanti ? 'var(--green)' : 'var(--accent3)', background: si.length >= appuntamentoSel.min_partecipanti ? 'rgba(74,222,128,0.1)' : 'rgba(255,120,87,0.08)', borderRadius: 8, padding: '6px 10px', border: `1px solid ${si.length >= appuntamentoSel.min_partecipanti ? 'rgba(74,222,128,0.3)' : 'rgba(255,120,87,0.2)'}` }}>
                  {si.length >= appuntamentoSel.min_partecipanti ? '✅' : '⚠️'} Min. partecipanti: {si.length}/{appuntamentoSel.min_partecipanti}
                </div>
              )}
            </div>
          </div>

          {/* Azioni stato */}
          {appuntamentoSel.stato === 'aperto' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button className="btn btn-primary" onClick={() => cambiaStato(appuntamentoSel, 'confermato')}>
                ✅ Conferma allenamento
              </button>
              <button className="btn btn-danger" onClick={() => cambiaStato(appuntamentoSel, 'annullato')}>
                ❌ Annulla allenamento
              </button>
            </div>
          )}
          {appuntamentoSel.stato !== 'aperto' && (
            <button className="btn btn-secondary" onClick={() => cambiaStato(appuntamentoSel, 'aperto')}>
              🔄 Riapri appuntamento
            </button>
          )}

          {/* Riepilogo adesioni */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">📊 Adesioni ({risposte.length})</div>
            </div>
            <div className="card-body" style={{ paddingTop: 0 }}>
              {risposte.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', padding: '20px 0' }}>
                  Nessuna risposta ancora
                </div>
              ) : (
                <>
                  {/* Ci sono */}
                  {si.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                        ✅ Ci sono ({si.length})
                      </div>
                      {si.map(r => (
                        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                          <div className="avatar" style={{ width: 32, height: 32, fontSize: 12, background: 'rgba(74,222,128,0.15)', color: 'var(--green)' }}>
                            {r.profili?.nome?.[0]}{r.profili?.cognome?.[0]}
                          </div>
                          <div style={{ fontSize: 13 }}>{r.profili?.nome} {r.profili?.cognome}</div>
                          <div style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--muted)' }}>
                            {new Date(r.risposto_il).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Non ci sono */}
                  {no.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                        ❌ Non ci sono ({no.length})
                      </div>
                      {no.map(r => (
                        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                          <div className="avatar" style={{ width: 32, height: 32, fontSize: 12, background: 'rgba(248,113,113,0.15)', color: 'var(--red)' }}>
                            {r.profili?.nome?.[0]}{r.profili?.cognome?.[0]}
                          </div>
                          <div style={{ fontSize: 13 }}>{r.profili?.nome} {r.profili?.cognome}</div>
                          <div style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--muted)' }}>
                            {new Date(r.risposto_il).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Elimina */}
          {confermaElimina ? (
            <div className="card slide-up" style={{ border: '1px solid rgba(248,113,113,0.3)' }}>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 700 }}>Eliminare questo appuntamento?</div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>Verranno eliminate anche tutte le risposte degli atleti.</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => setConfermaElimina(null)}>Annulla</button>
                  <button className="btn btn-danger" onClick={() => eliminaAppuntamento(confermaElimina)}>Elimina</button>
                </div>
              </div>
            </div>
          ) : (
            <button className="btn btn-ghost" style={{ color: 'var(--red)', fontSize: 13 }} onClick={() => setConfermaElimina(appuntamentoSel.id)}>
              🗑️ Elimina appuntamento
            </button>
          )}
        </div>
        {toast && <div className="toast">{toast}</div>}
      </div>
    )
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
      <TopBar onNotifiche={() => setMostraNotifiche(true)} />
      <div className="scroll-area">

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 18 }}>📣 Adesioni</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Proponi allenamenti e raccogli le disponibilità</div>
          </div>
          {gruppi.length > 0 && !mostraForm && (
            <button className="btn btn-primary btn-sm" onClick={() => setMostraForm(true)}>+ Nuovo</button>
          )}
        </div>

        {/* Form nuovo appuntamento */}
        {mostraForm && (
          <div className="card slide-up">
            <div className="card-header">
              <div className="card-title">📣 Nuovo appuntamento</div>
              <button onClick={() => { setMostraForm(false); resetForm() }} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Gruppo *</label>
                <select className="form-input" value={gruppoForm} onChange={e => setGruppoForm(e.target.value)}>
                  {gruppi.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Titolo *</label>
                <input className="form-input" type="text" value={titoloForm} onChange={e => setTitoloForm(e.target.value)} placeholder="Es. Allenamento CrossFit" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Data *</label>
                  <input className="form-input" type="date" value={dataForm} min={oggi} onChange={e => setDataForm(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Ora *</label>
                  <input className="form-input" type="time" value={oraForm} onChange={e => setOraForm(e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Min. partecipanti (opzionale)</label>
                <input className="form-input" type="number" min="0" value={minForm} onChange={e => setMinForm(e.target.value)} placeholder="Es. 3" />
              </div>
              <div className="form-group">
                <label className="form-label">Note (opzionale)</label>
                <input className="form-input" type="text" value={noteForm} onChange={e => setNoteForm(e.target.value)} placeholder="Es. Portare scarpe da ginnastica" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => { setMostraForm(false); resetForm() }}>Annulla</button>
                <button className="btn btn-primary" onClick={creaAppuntamento} disabled={salvataggio}>
                  {salvataggio ? 'Salvataggio...' : '📣 Crea e notifica'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toggle prossimi/passati — sempre visibile tranne se nessun gruppo */}
        {gruppi.length > 0 && !mostraForm && (
          <div style={{ display: 'flex', gap: 0, background: 'var(--surface2)', borderRadius: 10, padding: 3 }}>
            {[{ id: 'prossimi', label: '📅 Prossimi' }, { id: 'passati', label: '📂 Passati' }].map(v => (
              <button key={v.id} onClick={() => setVista(v.id)} style={{
                flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 700, fontFamily: 'Syne',
                background: vista === v.id ? 'var(--surface)' : 'transparent',
                color: vista === v.id ? 'var(--accent)' : 'var(--muted)',
                boxShadow: vista === v.id ? '0 1px 4px rgba(0,0,0,0.2)' : 'none',
              }}>{v.label}</button>
            ))}
          </div>
        )}

        {gruppi.length === 0 ? (
          <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: '30px 16px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, marginBottom: 8 }}>Nessun gruppo ancora</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>Crea un gruppo per poter proporre appuntamenti.</div>
            <button className="btn btn-primary" onClick={() => onCambiaTab && onCambiaTab('gruppi')}>Vai ai Gruppi</button>
          </div></div>
        ) : appFiltrati.length === 0 ? (
          <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: '30px 16px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📣</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, marginBottom: 8 }}>
              {vista === 'prossimi' ? 'Nessun appuntamento programmato' : 'Nessun appuntamento passato'}
            </div>
          </div></div>
        ) : (
          appFiltrati.map(app => {
            const dataFmt = new Date(app.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })
            const cnt = contatoriRisposte[app.id] || { si: 0, no: 0 }
            return (
              <div key={app.id} className="card" style={{ cursor: 'pointer' }} onClick={() => apriAppuntamento(app)}>
                <div className="card-header">
                  <div style={{ flex: 1 }}>
                    <div className="card-title">{app.titolo}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                      {app.gruppi?.nome} · {dataFmt} · {app.ora?.slice(0, 5)}
                    </div>
                    {(cnt.si > 0 || cnt.no > 0) && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 5 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)' }}>✅ {cnt.si}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--red)' }}>❌ {cnt.no}</span>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: 'var(--surface2)', color: statoColor[app.stato], whiteSpace: 'nowrap' }}>
                    {statoLabel[app.stato]}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}

/* ══════════════════════════════════════════
   VISTA ATLETA
══════════════════════════════════════════ */
function AdesioniAtleta({ profilo, onCambiaTab }) {
  const [appuntamenti, setAppuntamenti] = useState([])
  const [mieRisposte, setMieRisposte] = useState({}) // appuntamento_id -> risposta
  const [salvataggio, setSalvataggio] = useState(null)
  const [modificaAbilitata, setModificaAbilitata] = useState({}) // appuntamento_id -> bool
  const [toast, setToast] = useState('')
  const [vista, setVista] = useState('prossimi')
  const [mostraNotifiche, setMostraNotifiche] = useState(false)

  useEffect(() => { caricaDati() }, [])

  function toast_msg(msg) { setToast(msg); setTimeout(() => setToast(''), 3000) }

  async function caricaDati() {
    // Carica gruppi dell'atleta
    const { data: membriGruppi } = await supabase.from('gruppo_membri')
      .select('gruppo_id')
      .eq('utente_id', profilo.id)
      .eq('stato', 'attivo')

    if (!membriGruppi?.length) { setAppuntamenti([]); return }

    const gruppoIds = membriGruppi.map(m => m.gruppo_id)

    // Carica appuntamenti dei gruppi
    const { data: apps } = await supabase.from('adesioni_appuntamenti')
      .select('*, gruppi(nome)')
      .in('gruppo_id', gruppoIds)
      .order('data', { ascending: true })
      .order('ora', { ascending: true })
    setAppuntamenti(apps || [])

    // Carica risposte dell'atleta
    if (apps?.length) {
      const { data: risposte } = await supabase.from('adesioni_risposte')
        .select('appuntamento_id, risposta')
        .eq('utente_id', profilo.id)
        .in('appuntamento_id', apps.map(a => a.id))
      const map = {}
      risposte?.forEach(r => { map[r.appuntamento_id] = r.risposta })
      setMieRisposte(map)
    }
  }

  async function rispondi(app, risposta) {
    // Controlla limite 2h
    const adesso = new Date()
    const dataOra = new Date(`${app.data}T${app.ora}`)
    const diffOre = (dataOra - adesso) / (1000 * 60 * 60)
    if (diffOre < 2) {
      toast_msg('⏰ Non puoi più cambiare risposta (mancano meno di 2 ore)')
      return
    }

    // Se stava già rispondendo alla stessa risposta, non fare nulla
    if (mieRisposte[app.id] === risposta) return

    setSalvataggio(app.id)
    const vecchiaRisposta = mieRisposte[app.id]

    const { error } = await supabase.from('adesioni_risposte').upsert({
      appuntamento_id: app.id,
      utente_id: profilo.id,
      risposta,
      risposto_il: new Date().toISOString(),
    }, { onConflict: 'appuntamento_id,utente_id' })

    if (error) { toast_msg('Errore: ' + error.message); setSalvataggio(null); return }

    // Notifica al coach.
    // "Adesione cancellata" ha senso solo se l'atleta aveva davvero detto sì
    // prima: a chi rispondeva "no" la prima volta, il coach riceveva un titolo
    // che annunciava la cancellazione di una partecipazione mai dichiarata.
    const nome = `${profilo.nome} ${profilo.cognome || ''}`.trim()
    const haRitirato = vecchiaRisposta === 'si' && risposta === 'no'
    await supabase.from('notifiche').insert({
      utente_id: app.coach_id,
      tipo: 'adesione',
      titolo: risposta === 'si' ? '✅ Nuova adesione' : haRitirato ? '❌ Adesione ritirata' : '➖ Non parteciperà',
      testo: haRitirato
        ? `${nome} ha annullato la partecipazione a "${app.titolo}"`
        : risposta === 'si'
          ? `${nome} parteciperà a "${app.titolo}"`
          : `${nome} non parteciperà a "${app.titolo}"`,
      dati: JSON.stringify({ appuntamento_id: app.id }),
    })

    setMieRisposte(prev => ({ ...prev, [app.id]: risposta }))
    setModificaAbilitata(prev => ({ ...prev, [app.id]: false }))
    setSalvataggio(null)
    toast_msg(risposta === 'si' ? '✅ Hai confermato la partecipazione!' : '❌ Hai rifiutato la partecipazione')
  }

  async function rimuoviRisposta(app) {
    const adesso = new Date()
    const dataOra = new Date(`${app.data}T${app.ora}`)
    const diffOre = (dataOra - adesso) / (1000 * 60 * 60)
    if (diffOre < 2) {
      toast_msg('⏰ Non puoi più cambiare risposta (mancano meno di 2 ore)')
      return
    }

    setSalvataggio(app.id)
    await supabase.from('adesioni_risposte')
      .delete()
      .eq('appuntamento_id', app.id)
      .eq('utente_id', profilo.id)

    // Notifica coach se aveva detto sì
    if (mieRisposte[app.id] === 'si') {
      await supabase.from('notifiche').insert({
        utente_id: app.coach_id,
        tipo: 'adesione',
        titolo: '❌ Adesione ritirata',
        testo: `${profilo.nome} ${profilo.cognome || ''} ha annullato la partecipazione a "${app.titolo}"`.replace('  ', ' '),
        dati: JSON.stringify({ appuntamento_id: app.id }),
      })
    }

    setMieRisposte(prev => { const n = { ...prev }; delete n[app.id]; return n })
    setSalvataggio(null)
    toast_msg('Risposta rimossa')
  }

  const oraAdesso = new Date()
  const oggiStr = oraAdesso.toISOString().split('T')[0]

  const appFiltrati = appuntamenti.filter(a => {
    const passato = a.data < oggiStr || (a.data === oggiStr && a.ora < oraAdesso.toTimeString().slice(0, 8))
    return vista === 'prossimi' ? !passato : passato
  })

  const statoColor = { aperto: 'var(--accent)', confermato: 'var(--green)', annullato: 'var(--red)' }
  const statoLabel = { aperto: '🟡 Aperto', confermato: '✅ Confermato', annullato: '❌ Annullato' }

  if (mostraNotifiche) return <PaginaNotifiche onChiudi={() => setMostraNotifiche(false)} onNavigate={(dest) => { setMostraNotifiche(false); if (dest === 'adesioni' && onCambiaTab) onCambiaTab('adesioni') }} />

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
      <TopBar onNotifiche={() => setMostraNotifiche(true)} />
      <div className="scroll-area">
        <div>
          <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 18 }}>📣 Adesioni</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Dichiara la tua disponibilità agli allenamenti</div>
        </div>

        {/* Toggle */}
        <div style={{ display: 'flex', gap: 0, background: 'var(--surface2)', borderRadius: 10, padding: 3 }}>
          {[{ id: 'prossimi', label: '📅 Prossimi' }, { id: 'passati', label: '📂 Passati' }].map(v => (
            <button key={v.id} onClick={() => setVista(v.id)} style={{
              flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 700, fontFamily: 'Syne',
              background: vista === v.id ? 'var(--surface)' : 'transparent',
              color: vista === v.id ? 'var(--accent)' : 'var(--muted)',
              boxShadow: vista === v.id ? '0 1px 4px rgba(0,0,0,0.2)' : 'none',
            }}>{v.label}</button>
          ))}
        </div>

        {appFiltrati.length === 0 ? (
          <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: '30px 16px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📣</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, marginBottom: 8 }}>
              {vista === 'prossimi' ? 'Nessun appuntamento in programma' : 'Nessun appuntamento passato'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              {vista === 'prossimi' ? 'Il tuo coach non ha ancora proposto allenamenti.' : ''}
            </div>
          </div></div>
        ) : (
          appFiltrati.map(app => {
            const dataFmt = new Date(app.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })
            const miaRisposta = mieRisposte[app.id]
            const inSalvataggio = salvataggio === app.id
            const dataOra = new Date(`${app.data}T${app.ora}`)
            const diffOre = (dataOra - oraAdesso) / (1000 * 60 * 60)
            const bloccato = diffOre < 2 || app.stato === 'annullato'
            const puoCambiare = modificaAbilitata[app.id]

            return (
              <div key={app.id} className="card">
                <div className="card-header">
                  <div style={{ flex: 1 }}>
                    <div className="card-title">{app.titolo}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                      {app.gruppi?.nome} · {dataFmt} · {app.ora?.slice(0, 5)}
                    </div>
                    {app.note && (
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, fontStyle: 'italic' }}>{app.note}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: 'var(--surface2)', color: statoColor[app.stato], whiteSpace: 'nowrap' }}>
                    {statoLabel[app.stato]}
                  </div>
                </div>

                {app.stato !== 'annullato' && (
                  <div className="card-body" style={{ paddingTop: 0 }}>
                    {bloccato && diffOre < 2 ? (
                      /* Risposte chiuse — mostra solo la scelta fatta */
                      <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: '8px 0' }}>
                        ⏰ Risposte chiuse (mancano meno di 2 ore)
                        {miaRisposta && (
                          <span style={{ color: miaRisposta === 'si' ? 'var(--green)' : 'var(--red)', marginLeft: 6, fontWeight: 700 }}>
                            · {miaRisposta === 'si' ? '✅ Sei disponibile' : '❌ Non sei disponibile'}
                          </span>
                        )}
                      </div>
                    ) : miaRisposta && !puoCambiare ? (
                      /* Ha già risposto — mostra scelta e bottone cambia */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{
                          padding: '12px 14px', borderRadius: 10, textAlign: 'center',
                          background: miaRisposta === 'si' ? 'rgba(74,222,128,0.15)' : 'rgba(248,113,113,0.15)',
                          border: `2px solid ${miaRisposta === 'si' ? 'var(--green)' : 'var(--red)'}`,
                          color: miaRisposta === 'si' ? 'var(--green)' : 'var(--red)',
                          fontFamily: 'Syne', fontWeight: 700, fontSize: 14,
                        }}>
                          {miaRisposta === 'si' ? '✅ Hai confermato la partecipazione' : '❌ Hai rifiutato la partecipazione'}
                        </div>
                        <button
                          onClick={() => setModificaAbilitata(prev => ({ ...prev, [app.id]: true }))}
                          style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', padding: '2px 0' }}>
                          Cambia risposta
                        </button>
                      </div>
                    ) : (
                      /* Non ha ancora risposto oppure sta cambiando — mostra bottoni */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {puoCambiare && (
                          <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>
                            Risposta attuale: <strong style={{ color: miaRisposta === 'si' ? 'var(--green)' : 'var(--red)' }}>
                              {miaRisposta === 'si' ? '✅ Ci sono' : '❌ Non ci sono'}
                            </strong> — scegli la nuova:
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => rispondi(app, 'si')}
                            disabled={inSalvataggio}
                            style={{
                              flex: 1, padding: '10px', borderRadius: 10, cursor: inSalvataggio ? 'default' : 'pointer',
                              fontFamily: 'Syne', fontWeight: 700, fontSize: 13, border: '1px solid rgba(74,222,128,0.3)',
                              background: 'rgba(74,222,128,0.08)', color: 'var(--green)',
                              opacity: inSalvataggio ? 0.5 : 1,
                            }}>
                            ✅ Ci sono
                          </button>
                          <button
                            onClick={() => rispondi(app, 'no')}
                            disabled={inSalvataggio}
                            style={{
                              flex: 1, padding: '10px', borderRadius: 10, cursor: inSalvataggio ? 'default' : 'pointer',
                              fontFamily: 'Syne', fontWeight: 700, fontSize: 13, border: '1px solid rgba(248,113,113,0.3)',
                              background: 'rgba(248,113,113,0.08)', color: 'var(--red)',
                              opacity: inSalvataggio ? 0.5 : 1,
                            }}>
                            ❌ Non ci sono
                          </button>
                        </div>
                        {puoCambiare && (
                          <button
                            onClick={() => setModificaAbilitata(prev => ({ ...prev, [app.id]: false }))}
                            style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', padding: '2px 0' }}>
                            Annulla
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}
